import { GENERIC_PLAN_LOGO, resolvePlanLogo } from './planDisplay';
import { findArcaPlanById } from '../../constants/arcaPlans';
import {
	isLivePlan,
	isPersonalViewPlan,
	PENDING_PERSONAL_OPTION_KEY,
	RESUMEN_OPTION_KEY,
	resolveViewOptions,
	ViewOptionPlan
} from '../../utils/viewOptions';
import { ViewType } from '../../types/dashboard';

/**
 * Lo que el selector necesita de un plan para pintarlo. Es `ViewOptionPlan`
 * —el subconjunto que ya usa el selector de vistas— más los dos datos de
 * presentación: la gestora (su logo) y el saldo.
 */
export interface SelectablePlan extends ViewOptionPlan {
	managerName: string | null;
	vestedRightsCents: number | null;
}

/** Lo que el selector sabe además de los planes. */
export interface PlanSelectorOptions {
	/** Alta individual del funnel viva y sin plan personal todavía. */
	pendingPersonalPlan?: boolean;
}

export const PENDING_PERSONAL_PLAN_NAME_KEY = 'general.avatar.pendingPersonalPlan';

/** El plan de Arca que eligió en el funnel, si es uno que conocemos. */
const pendingArcaPlan = (arcaPlanId: string | null | undefined) =>
	arcaPlanId ? findArcaPlanById(arcaPlanId) : undefined;

/**
 * El plan en proceso se nombra por el plan que eligió en el funnel, como el resto
 * del selector (decisión de producto, 15/09/26). Si no se reconoce, «Plan individual».
 */
export const pendingPlanName = (
	arcaPlanId: string | null | undefined,
	translate: Translate
): string => pendingArcaPlan(arcaPlanId)?.name ?? translate(PENDING_PERSONAL_PLAN_NAME_KEY);

/** El logo del plan en proceso: el de su gestora, o el genérico si no se reconoce. */
export const resolvePendingPlanLogo = (arcaPlanId: string | null | undefined): string => {
	const plan = pendingArcaPlan(arcaPlanId);
	return plan ? resolvePlanLogo(plan.name, plan.manager) : GENERIC_PLAN_LOGO;
};

/**
 * 🔴 El plan de EMPLEO va con un nombre genérico y NO con el de su producto: es como lo llama la
 * persona («mi plan de empleo»), y dos empleos distintos pueden compartir producto. Lo que los
 * distingue es la empresa, que añade Proa («Plan de empleo · Empresa»). Los personales, al revés:
 * los separa su nombre comercial, o el genérico si no lo tienen.
 */
export const EMPLOYMENT_PLAN_KEY = 'general.avatar.employmentPlan';
export const UNNAMED_PLAN_KEY = 'general.avatar.unnamedPlan';

type Translate = (key: string) => string;

/**
 * Los planes que ofrece el desplegable, en su orden.
 *
 * 🔴 El orden se toma prestado de `resolveViewOptions` en vez de repetirlo: ahí
 * ya está resuelto y probado (empleo antes que personales, activo antes que
 * parado, el más antiguo primero, y el id como desempate para que no bailen
 * entre renders). Un segundo criterio aquí acabaría discrepando, y entonces la
 * pastilla diría un plan y la lista ofrecería otro primero.
 *
 * Se le pasan roles vacíos a propósito: la entrada de Admin no es un plan y
 * este desplegable no la lista.
 */
export const orderedSelectablePlans = (plans: SelectablePlan[]): SelectablePlan[] => {
	const byId = new Map(plans.map((plan) => [plan.id, plan]));
	// `String(...)` en vez de comprobar el `planId`: una opción que no sea un
	// plan (la de Admin, o el fallback sin lista) sencillamente no está en el
	// mapa y se cae en el filtro. Preguntar antes sería una rama que con estas
	// entradas no se puede alcanzar, y un test que la cubriera sería inventado.
	return resolveViewOptions(plans, [])
		.map((option) => byId.get(String(option.planId)))
		.filter((plan): plan is SelectablePlan => Boolean(plan));
};

/**
 * Qué plan corresponde a la VISTA en la que se está.
 *
 * 🔴 No basta con el plan personal elegido. El Dashboard lo preselecciona al
 * montar se mire lo que se mire, y elegir el de empleo no lo toca a propósito
 * —ese handle lo consume la vista personal como «su» plan—. Sin esto, un
 * empleado con plan propio leía el nombre de su plan personal en la pastilla
 * mientras las secciones y el contenido eran los de su empleo.
 *
 * En la vista personal manda el elegido; si ya no está en la lista, el primero
 * que haya, que es el que la vista sirve.
 *
 * Sin plan personal y con uno en proceso, la vista personal es la de ese plan en
 * proceso.
 */
export const resolvePlanIdForView = (
	plans: SelectablePlan[],
	viewType: string,
	selectedPersonalPlanId: string | null | undefined,
	{ pendingPersonalPlan = false }: PlanSelectorOptions = {}
): string | null => {
	if (viewType === ViewType.RESUMEN) return RESUMEN_OPTION_KEY;
	const live = plans.filter(isLivePlan);
	if (viewType === ViewType.INDIVIDUAL) {
		const personal = live.filter(isPersonalViewPlan);
		const chosen = personal.find((plan) => plan.id === selectedPersonalPlanId);
		if (!chosen && personal.length === 0 && pendingPersonalPlan) return PENDING_PERSONAL_OPTION_KEY;
		return (chosen ?? personal[0] ?? live[0])?.id ?? null;
	}
	// Vista de empleado: su plan de empleo. Un B2C no tiene, y entonces manda el
	// que la vista esté sirviendo, que es el primero de su lista.
	const employment = live.find((plan) => !isPersonalViewPlan(plan));
	return (
		(employment ?? live.find((plan) => plan.id === selectedPersonalPlanId) ?? live[0])?.id ?? null
	);
};
